import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useRole, useStaffPermissions } from '@structural-climbing/auth'
import { Button, Spinner } from '@structural-climbing/ui'
import { useTrainerAssignments, type TrainerAssignment } from './useTrainerAssignments'
import { AssignTrainerModal } from './AssignTrainerModal'
import { ExtendAssignmentModal } from './ExtendAssignmentModal'
import { ReassignTrainerModal } from './ReassignTrainerModal'

interface Props {
  memberUserId: string
  memberEmail: string
  memberName: string
  /**
   * The child whose trainers to show, on the child's own profile. Omitted on
   * the adult's profile, which then shows the adult's own rows only: a parent
   * who is also a trainer coaching their kids otherwise saw themselves listed
   * once per child.
   */
  childId?: string | null
}

function formatRange(a: TrainerAssignment, locale: string): string {
  const fmt = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(locale)
  return a.ends_at ? `${fmt(a.starts_at)} – ${fmt(a.ends_at)}` : fmt(a.starts_at)
}

/**
 * A member's trainers, shown inside their profile. Past assignments stay listed
 * below the current ones: when a member moves between coaches mid-contract the
 * gym needs to see who had them and when, which is the whole reason ended rows
 * are kept rather than overwritten.
 */
export function TrainerAssignmentsSection({
  memberUserId,
  memberEmail,
  memberName,
  childId = null,
}: Props) {
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  // Assignments are stored against the parent's account, children included,
  // so one fetch serves both profiles and the person is picked out here.
  const { assignments: all, loading, reload } = useTrainerAssignments(memberUserId)
  const assignments = all.filter((a) => a.child_id === childId)

  // A trainer can read this section for their own members but cannot change it:
  // every write endpoint requires the gym-wide `members` permission. Showing
  // them Assign and End would only produce a 403 they cannot act on.
  const role = useRole()
  const staffPermissions = useStaffPermissions()
  const canManage = role === 'gym_owner' || staffPermissions?.members === true

  const [assignOpen, setAssignOpen] = useState(false)
  const [endingId, setEndingId] = useState<string | null>(null)
  const [extendTarget, setExtendTarget] = useState<TrainerAssignment | null>(null)
  const [reassignTarget, setReassignTarget] = useState<TrainerAssignment | null>(null)

  const current = assignments.filter((a) => a.status === 'current')
  const upcoming = assignments.filter((a) => a.status === 'upcoming')
  const past = assignments.filter((a) => a.status === 'past')

  async function handleEnd(id: string) {
    setEndingId(id)
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${apiUrl}/trainer-assignments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      await reload()
    } finally {
      setEndingId(null)
    }
  }

  function row(a: TrainerAssignment, isPast: boolean) {
    const showActions = canManage && !isPast
    return (
      <div
        key={a.id}
        className="flex flex-col gap-2 rounded-lg bg-content2/50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
      >
        <div className="min-w-0">
          <p className={`truncate text-sm ${isPast ? 'text-foreground/50' : ''}`}>
            {a.trainer_name ?? t('trainers.unknownTrainer')}
          </p>
          <p className="text-xs text-foreground/40">
            {formatRange(a, i18n.language)}
            {isPast && a.end_reason ? ` · ${t(`trainers.endReason.${a.end_reason}`)}` : ''}
          </p>
        </div>
        {showActions && (
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            <Button size="sm" variant="flat" onClick={() => setExtendTarget(a)}>
              {t('trainers.extendAction')}
            </Button>
            <Button size="sm" variant="flat" onClick={() => setReassignTarget(a)}>
              {t('trainers.reassignAction')}
            </Button>
            <Button
              size="sm"
              variant="flat"
              color="danger"
              isDisabled={endingId === a.id}
              onClick={() => void handleEnd(a.id)}
            >
              {endingId === a.id ? t('trainers.ending') : t('trainers.endAction')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
          {t('trainers.sectionTitle')}
        </p>
        {canManage && (
          <Button size="sm" variant="light" color="primary" onClick={() => setAssignOpen(true)}>
            {t('trainers.assignAction')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-3">
          <Spinner size="sm" color="primary" />
        </div>
      ) : assignments.length === 0 ? (
        <p className="text-sm text-foreground/30">{t('trainers.noneAssigned')}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {current.map((a) => row(a, false))}
          {upcoming.length > 0 && (
            <>
              <p className="mt-2 text-xs text-foreground/30">{t('trainers.upcomingTitle')}</p>
              {upcoming.map((a) => row(a, false))}
            </>
          )}
          {past.length > 0 && (
            <>
              <p className="mt-2 text-xs text-foreground/30">{t('trainers.pastTitle')}</p>
              {past.map((a) => row(a, true))}
            </>
          )}
        </div>
      )}

      {extendTarget && (
        <ExtendAssignmentModal
          assignment={extendTarget}
          onClose={() => setExtendTarget(null)}
          onSaved={() => void reload()}
        />
      )}

      {reassignTarget && (
        <ReassignTrainerModal
          assignment={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onReassigned={() => void reload()}
        />
      )}

      {assignOpen && (
        <AssignTrainerModal
          memberUserId={memberUserId}
          memberEmail={memberEmail}
          memberName={memberName}
          childId={childId}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => void reload()}
        />
      )}
    </div>
  )
}
